import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import DropdownButton from "react-bootstrap/DropdownButton";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Dropdown from "react-bootstrap/Dropdown";

import ModalSelectGrouped from "site-apps/assets/components/ModalSelectGrouped";
import ModalWaypointSelects from "site-apps/distribution/waypoints/ModalWaypointSelects";
import {RouteWaypointModel} from "site-apps/distribution/route/RouteWaypointModel";
import {subscribeEvent, unsubscribeEvent} from "core/eventsConnector";
import FormRouteIslandsDragDrop from "./FormRouteIslandsDragDrop";
import FormRouteIslandsList from "./FormRouteIslandsList";
import ConditionalRender from "../../../../components/ConditionalRender";

const FormRouteIslands = ({formData, waypoints, setWaypoints, setHasChanged, dataChanged, highlighted, setHighligthed, canSave}) => {
    const {t: _Tf} = useTranslation("forms");
    const [wayAssets, setWayAssets] = useState([]);
    const [wayRefs, setWayRefs] = useState([]);
    const [showModalAssets, setShowModalAssets] = useState(false);
    const [showModalWaypoints, setShowModalWaypoints] = useState(false);
    const [filtersModalAssets, setFiltersModalAssets] = useState({waste_type: "", download_type: ""});

    useEffect(() => {
        if (waypoints.length) {
            dataChanged.current.waypoints = [...waypoints];
            const wayAssets_ = [];
            const wayRefs_ = [];
            waypoints.forEach((_waypoint, idx) => {
                const waypoint_ = new RouteWaypointModel(_waypoint);
                if (waypoint_.is_asset) {
                    waypoint_.createAsset();
                    wayAssets_.push(waypoint_.asset);
                } else if (waypoint_.mooch) {
                    waypoint_.createMooch();
                    wayAssets_.push(waypoint_.asset);
                } else {
                    waypoint_.createWaypoint();
                    wayRefs_.push(waypoint_.waypoint);
                }
            });
            setWayAssets([...wayAssets_]);
            setWayRefs([...wayRefs_]);
        }
        return () => {
            unsubscribeEvent("assets:modal:selector", eventSelectAssets);
            unsubscribeEvent("waypoints:modal:selector", eventSelectWaypoint);
        };
    }, [waypoints]);

    const onClickSelectWaypoints = () => {
        subscribeEvent("waypoints:modal:selector", eventSelectWaypoint);
        setShowModalWaypoints(true);
    };
    const eventSelectWaypoint = (e) => {
        let currentIds = [];
        let selectedIds = [];
        waypoints.forEach((_point) => {
            if (!_point.is_asset) {
                currentIds.push(_point.waypoint_id);
            }
        });
        let waypoints_ = [...waypoints];
        let wayRefs_ = [...wayRefs];
        let orders = waypoints.length;
        e.detail.forEach((_waypoint) => {
            selectedIds.push(_waypoint.id);
            if (!currentIds.includes(_waypoint.id)) {
                orders++;
                let point = new RouteWaypointModel();
                _waypoint.order = orders;
                point.setWaypoint(_waypoint);
                waypoints_.push(point);
                wayRefs_.push(_waypoint);
            }
        });

        if (selectedIds.length) {
            waypoints_ = waypoints_.filter((_point) => {
                if (!_point.is_asset) {
                    return selectedIds.includes(_point.waypoint_id);
                }
                return true;
            });
            wayRefs_ = wayRefs_.filter((_ref) => selectedIds.includes(_ref.id));
        }
        dataChanged.current.waypoints = [...waypoints_];
        setWaypoints(waypoints_);
        setWayRefs(wayRefs_);
        setHasChanged(true);
        unsubscribeEvent("waypoints:modal:selector", eventSelectWaypoint);
    };

    const onClickSelectAssets = () => {
        setFiltersModalAssets({
            waste_type: formData.waste_type,
            download_type: formData.download_type,
        });
        setShowModalAssets(true);
        subscribeEvent("assets:modal:selector", eventSelectAssets);
    };

    function eventSelectAssets(e) {
        let currentIds = [];
        waypoints.forEach((_point) => {
            if (_point.is_asset && !_point.is_island) {
                currentIds.push(_point.asset_id);
            }
        });
        const islandCoveredIds = new Set();
        const selectedAssetIds = new Set();
        const selectedIslandIds = new Set();
        e.detail.assets.forEach((_asset) => {
            if (_asset.is_island) {
                selectedIslandIds.add(_asset.id);
                _asset.asset_ids.forEach((aid) => islandCoveredIds.add(aid));
            } else {
                selectedAssetIds.add(_asset.id);
            }
        });
        const selectedIslands = {};
        e.detail.assets.forEach((_asset) => {
            if (_asset.is_island) {
                selectedIslands[_asset.id] = _asset;
            }
        });
        let wayAssets_ = wayAssets.filter((_asset) =>
            selectedAssetIds.has(_asset.id) || selectedIslandIds.has(_asset.id)
        );
        // se recorren los puntos actuales en su orden: las referencias no se mueven y un contenedor que ahora
        // llega dentro de su isla se sustituye por la isla en la posición del primero de sus contenedores
        const placedIslandIds = new Set();
        let waypoints_ = [];
        waypoints.forEach((_point) => {
            // los puntos que vienen del backend traen island_id pero no is_island
            if (!_point.is_asset) {
                waypoints_.push(_point);
            } else if (_point.is_island || _point.island_id) {
                if (selectedIslandIds.has(_point.island_id) && !placedIslandIds.has(_point.island_id)) {
                    placedIslandIds.add(_point.island_id);
                    waypoints_.push(_point);
                }
            } else if (selectedAssetIds.has(_point.asset_id)) {
                waypoints_.push(_point);
            } else if (islandCoveredIds.has(_point.asset_id)) {
                const island = Object.values(selectedIslands).find((_island) =>
                    _island.asset_ids.includes(_point.asset_id)
                );
                if (!placedIslandIds.has(island.id)) {
                    placedIslandIds.add(island.id);
                    let point = new RouteWaypointModel();
                    point.setIsland(island);
                    waypoints_.push(point);
                    if (!wayAssets_.some((_asset) => _asset.is_island && _asset.id === island.id)) {
                        wayAssets_.push(island);
                    }
                }
            }
        });
        // al final, solo lo nuevo
        e.detail.assets.forEach((_asset) => {
            if (_asset.is_island) {
                if (!placedIslandIds.has(_asset.id)) {
                    placedIslandIds.add(_asset.id);
                    let point = new RouteWaypointModel();
                    point.setIsland(_asset);
                    waypoints_.push(point);
                    wayAssets_.push(_asset);
                }
            } else if (!currentIds.includes(_asset.id) && !islandCoveredIds.has(_asset.id)) {
                let point = new RouteWaypointModel();
                point.setAsset(_asset);
                waypoints_.push(point);
                wayAssets_.push(_asset);
            }
        });
        waypoints_.forEach((_point, idx) => {
            _point.order = idx + 1;
        });
        dataChanged.current.waypoints = [...waypoints_];
        setWaypoints(waypoints_);
        setWayAssets(wayAssets_);
        setHasChanged(true);
        unsubscribeEvent("assets:modal:selector", eventSelectAssets);
    }

    return canSave ? (
        <>
            <div className="row">
                <div className="col h5">{_Tf("routes.itinerary")}</div>
                <div className="col text-end">
                    <button type="button" className="btn btn-outline-warning me-2 btn-sm" title={_Tf("common.clean")}>
                        <FontAwesomeIcon icon="broom" />
                    </button>
                    <DropdownButton
                        size="sm"
                        align="end"
                        as={ButtonGroup}
                        id="dropdown-selector-containers"
                        variant="outline-primary"
                        title={<FontAwesomeIcon icon="pen" />}>
                        <Dropdown.Item eventKey="w" onClick={onClickSelectWaypoints}>
                            <FontAwesomeIcon icon="map-location-dot" /> {_Tf("routes.references")}
                        </Dropdown.Item>
                        <Dropdown.Item eventKey="a" onClick={onClickSelectAssets}>
                            <FontAwesomeIcon icon="dumpster" /> {_Tf("routes.containers")}
                        </Dropdown.Item>
                    </DropdownButton>
                </div>
                <div className="col-12 ps-4 pe-4 mt-3">
                    <FormRouteIslandsDragDrop
                        waypoints={waypoints}
                        setWaypoints={setWaypoints}
                        dataChanged={dataChanged}
                        setHasChanged={setHasChanged}
                        highlighted={highlighted}
                        setHighligthed={setHighligthed}
                        wayAssets={wayAssets}
                        setWayAssets={setWayAssets}
                        wayRefs={wayRefs}
                        setWayRefs={setWayRefs}
                        wasteType={formData.waste_type}
                    />
                </div>
            </div>
            <ConditionalRender condition={showModalAssets}>
                <ModalSelectGrouped
                    show={showModalAssets}
                    setShow={setShowModalAssets}
                    filtering={filtersModalAssets}
                    chosenIn={wayAssets}
                    multiple={true}
                    subtitle={_Tf("labels.route") + ": " + formData.name}
                    aria-modal="true"
                />
            </ConditionalRender>
            <ConditionalRender condition={showModalWaypoints}>
                <ModalWaypointSelects
                    show={showModalWaypoints}
                    setShow={setShowModalWaypoints}
                    chosenIn={wayRefs}
                    aria-modal="true"
                />
            </ConditionalRender>
        </>
    ) : (
        <div className="row">
            <div className="col h5">{_Tf("routes.itinerary")}</div>
            <div className="col-12 ps-4 pe-4 mt-3">
                <FormRouteIslandsList
                    waypoints={waypoints}
                    highlighted={highlighted}
                    setHighligthed={setHighligthed}
                    wasteType={formData.waste_type}
                />
            </div>
        </div>
    );
};
export default FormRouteIslands;
